import { Alert, Badge, Box, Button, Container, Group, Stack, Table, Text, Title } from '@mantine/core';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
import { useState } from 'react';
import Breadcrumb from '@components/breadcrumb';
import TitleBar from '@components/title-bar';
import NextNav from '@components/next-nav';
import GraphqlError from '@components/graphql-error';
import LoadingData from '@components/graphql-loading-data';
import { GET_NARRATIVE_VERSIONS, GET_NARRATIVE_DOWNLOAD_URL } from '@queries/narrative';

const statusColor = (status: string) => {
  switch (status) {
    case 'complete':
      return 'green';
    case 'error':
      return 'red';
    case 'pending':
    case 'generating':
    case 'building_document':
      return 'yellow';
    default:
      return 'gray';
  }
};

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

function Narratives() {
  const { clientId, tripProfileId = '' } = useParams();
  const navigate = useNavigate();
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const { loading, error, data } = useQuery(GET_NARRATIVE_VERSIONS, {
    variables: { tripProfileId },
    skip: !tripProfileId,
    fetchPolicy: 'cache-and-network',
  });

  const [fetchDownloadUrl] = useLazyQuery(GET_NARRATIVE_DOWNLOAD_URL, {
    fetchPolicy: 'network-only',
  });

  if (!clientId) return <Text c="red">No client ID provided.</Text>;
  if (!tripProfileId) return <Text c="red">No trip profile ID provided.</Text>;
  if (loading) return <LoadingData title="Loading Narratives..." />;
  if (error) return <GraphqlError error={error} />;

  const versions = data?.getNarrativeVersions ?? [];

  const handleDownload = async (id: string) => {
    setDownloadError(null);
    setDownloadingId(id);
    try {
      const result = await fetchDownloadUrl({ variables: { narrativeVersionId: id } });
      const url = result.data?.getNarrativeDownloadUrl;
      if (!url) {
        setDownloadError('No download available for this narrative version.');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Failed to fetch download URL.');
    } finally {
      setDownloadingId(null);
    }
  };

  const goGenerateNew = () => navigate(`/clients/${clientId}/trip-profiles/${tripProfileId}/narrative-input`);

  return (
    <div>
      <Breadcrumb pb={30} pageStep={7} />
      <TitleBar mb={0} />
      <Box bg="var(--mantine-color-voyTitleBox-6)" p={10} mb={30} mt={0}>
        <Title order={2} c="white" ta="center">
          VoyLogic Itinerary
        </Title>
      </Box>

      <Container fluid>
        <Group justify="space-between" mb={20}>
          <Text size="sm" c="dimmed">
            {versions.length === 0
              ? 'No narratives have been generated for this trip yet.'
              : `${versions.length} narrative${versions.length === 1 ? '' : 's'} generated for this trip.`}
          </Text>
          <Button variant="filled" onClick={goGenerateNew}>
            Generate New
          </Button>
        </Group>

        {downloadError && (
          <Alert color="red" variant="light" mb={20} withCloseButton onClose={() => setDownloadError(null)}>
            {downloadError}
          </Alert>
        )}

        {versions.length > 0 && (
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Version</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th ta="right">Download</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {versions.map((v) => {
                const canDownload = v.status === 'complete' && !!v.docS3Key;
                return (
                  <Table.Tr key={v.id}>
                    <Table.Td>v{v.version}</Table.Td>
                    <Table.Td>{formatDate(v.createdAt)}</Table.Td>
                    <Table.Td>
                      <Badge color={statusColor(v.status)} variant="light">
                        {v.status}
                      </Badge>
                      {v.status === 'error' && v.errorMessage && (
                        <Text size="xs" c="red.7" mt={4}>
                          {v.errorMessage}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td ta="right">
                      <Button
                        size="xs"
                        variant={canDownload ? 'filled' : 'subtle'}
                        disabled={!canDownload}
                        loading={downloadingId === v.id}
                        onClick={() => handleDownload(v.id)}
                      >
                        Download
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}

        {versions.length === 0 && (
          <Stack align="center" gap="xs" mt={40}>
            <Text c="dimmed">Click "Generate New" to create your first narrative for this trip.</Text>
          </Stack>
        )}
      </Container>

      <NextNav saveVisible={false} done={`/clients/${clientId}`} />
    </div>
  );
}

export default Narratives;
